import {skipToken} from '@reduxjs/toolkit/query';
import {match, Pattern} from 'ts-pattern';

import {useState} from 'react';
import {useDispatch} from 'react-redux';

import {equals, isNil} from 'ramda';

import {
  newVehicleApi,
  useGetModelYearQuery,
  type PricelistVersionRequestBody,
} from '@dms/api/new-vehicle';

import {isNotNilOrEmpty} from 'shared/web';

import type {
  NewPricelistVersionDialogProps,
  PricedItemChangeRequest,
  PublishedPriceChange,
} from '../components/NewPricelistVersionDialog';
import type {PricedItemBatchEntry} from '../types/PricedItemBatchEntry';
import type {PricedItemChange} from '../types/PricedItemChange';
import {
  ITEM_CHANGE_CANCELLED,
  type PricedItemChangeOutcome,
} from '../types/PricedItemChangeOutcome';
import {PricedItemGroupBuildFailure} from '../types/PricedItemGroupBuildFailure';
import {PricedItemGroupRefusal} from '../types/PricedItemGroupRefusal';
import {addEntriesToGroup, addToGroup} from '../utils/addEntriesToGroup';
import {buildBatchOperations, MAX_BATCH_OPERATIONS} from '../utils/buildBatchOperations';
import {catalogueErrorCodes} from '../utils/catalogueUi';
import {failedEntryIndex} from '../utils/failedEntryIndex';
import {pricedItemGroupKeys} from '../utils/pricedItemGroupKeys';
import {pricedItemGroupStore} from '../utils/pricedItemGroupStore';
import {keepUnknownOutcome, reviewedGroupBatch, rowsOf} from '../utils/reviewedGroupBatch';
import {useItemChangeDialogs} from './useItemChangeDialogs';
import {useOpenPricedItemGroup} from './useOpenPricedItemGroup';
import {useSendPricedItemBatch} from './useSendPricedItemBatch';

const ZERO_PRICE = '0.00';
const PUBLISHED_PRICE_CHANGE = 'DMS-NV-CT-0054';
const STALE_CATALOGUE_VERSION = 'DMS-NV-CT-0012';

const isSamePrice = (price: string, storedPrice: string) => Number(price) === Number(storedPrice);

/**
 * The key of the entry is the key of an entry of the open group, as addEntriesToGroup compares
 * them. A new matrix cell is not: the grouped matrix PUT reads the matrix again at its build.
 */
const isTargetInGroup = (yearId: string, entry: Pick<PricedItemBatchEntry, 'key'>) =>
  !pricedItemGroupKeys.isMatrixPut(entry.key) &&
  (pricedItemGroupStore.get(yearId)?.entries.some(({key}) => key === entry.key) ?? false);

/**
 * The batch of the open group of the year, built from fresh reads: 'empty' without entries,
 * 'written' when no entry has an operation left (the entries leave the group). A failed build
 * rejects with PricedItemGroupBuildFailure, which names the entry; a batch of more operations than
 * the BE takes, with a PricedItemGroupRefusal. The group counts the operations of each entry when it
 * is added; a build reads the stored data again and can return more.
 */
const buildGroupBatch = async (yearId: string) => {
  const group = pricedItemGroupStore.get(yearId);
  if (isNil(group) || group.entries.length === 0) {
    return 'empty' as const;
  }
  const {entries} = group;
  const built = await buildBatchOperations(entries);
  if (built.outcome === 'buildFailed') {
    throw new PricedItemGroupBuildFailure(entries[built.entryIndex], built.error);
  }
  if (built.operations.length === 0) {
    pricedItemGroupStore.removeSent(yearId, entries);
    return 'written' as const;
  }
  if (built.operations.length > MAX_BATCH_OPERATIONS) {
    throw new PricedItemGroupRefusal('batchTooLarge');
  }
  return {entries, built};
};

/**
 * A published model year refuses a direct price change (DMS-NV-CT-0054). Its editors save
 * everything else first, then hand the price to NewPricelistVersionDialog as a mid-year adjustment.
 */
export function usePublishedPriceSave(modelYearId: string | undefined) {
  const {data: modelYear} = useGetModelYearQuery(isNil(modelYearId) ? skipToken : {modelYearId});
  const [getModelYear] = newVehicleApi.useLazyGetModelYearQuery();
  const {send: sendBatch} = useSendPricedItemBatch();
  const {isGroupOpen, resolveUnknownOutcome} = useOpenPricedItemGroup();
  const dispatch = useDispatch();
  const [change, setChange] = useState<PublishedPriceChange | PricedItemChangeRequest>();
  const {confirmItemChange} = useItemChangeDialogs(setChange);

  /**
   * The year as cached, or read again when the cached read failed: a failed refresh leaves the
   * cache rejected, which would otherwise refuse every later save until something refetched it.
   */
  const readKnownYear = (id: string) =>
    getModelYear({modelYearId: id}, true)
      .unwrap()
      .catch(() => getModelYear({modelYearId: id}, false).unwrap());

  /**
   * A direct write refused with DMS-NV-CT-0054 was sent on a stale read: another author published
   * the year, or a trim, after it was read. Reads the year and the authoring lists (matrix, trims,
   * packages) again, so the next try asks for a pricelist version, then rejects with the error.
   */
  const rejectDirectWrite = (error: unknown): never => {
    if (catalogueErrorCodes(error).includes(PUBLISHED_PRICE_CHANGE)) {
      dispatch(
        newVehicleApi.util.invalidateTags([
          {type: 'NewVehicleCatalogueResource', id: 'modelYear'},
          'NewVehicleCatalogueAuthoring',
        ])
      );
    }
    throw error;
  };

  /**
   * On a published year, runs `saveFirst` and opens the confirm with the model-year version that
   * save produced. Resolves false on a draft year, or for an item outside the price list, before
   * anything is sent. Rejects with a PricedItemGroupRefusal while a group of the year is open.
   */
  const confirmAdjustment = async (
    adjustment: PublishedPriceChange['adjustment'],
    options: {
      saveFirst?: {saved: NonNullable<PublishedPriceChange['saved']>; run: () => Promise<unknown>};
      /**
       * The item is outside the price list (by its stored state: a Draft trim or package, a Variant
       * of a Draft trim, a disabled Variant): its price is written directly, as on a draft year.
       */
      isDraftItem?: boolean;
      onDone: () => void;
    }
  ) => {
    if (isNil(modelYearId) || options.isDraftItem) {
      return false;
    }
    // Waits for the first load, so a save made before the status is known is not sent directly.
    const known = await readKnownYear(modelYearId);
    if (known.status !== 'active') {
      return false;
    }
    // An adjustment applies at once and moves the label, while the open group lives only in the
    // browser and is not confirmed yet (Q3). Every adjustment is refused before anything is sent.
    if (await isGroupOpen(modelYearId)) {
      throw new PricedItemGroupRefusal('priceChangeOfExistingRow');
    }
    // The other changes asked for a new pricelist version first, which was cancelled: nothing
    // else is sent, and the editor stays open as while a confirm is shown.
    if ((await options.saveFirst?.run()) === ITEM_CHANGE_CANCELLED) {
      return true;
    }
    setChange({
      mode: 'adjustment',
      adjustment,
      modelYear: options.saveFirst ? await getModelYear({modelYearId}, false).unwrap() : known,
      saved: options.saveFirst?.saved,
      onDone: options.onDone,
    });
    return true;
  };

  /**
   * An editor save. A changed price on a published year is held back: the other changed fields save
   * with the stored price, so their errors stay in the editor, then the confirm sets the price.
   * Resolves true when the confirm took over; otherwise the whole save ran and `onDone` was called.
   */
  const save = async <V extends Record<K, string>, K extends keyof V>(form: {
    values: V;
    /** The record as the server holds it; the price compares against this one. */
    storedValues: V;
    priceField: K;
    /** A save that stopped half way runs again, even when no field differs from the stored values. */
    isSavePending?: boolean;
    scope: PublishedPriceChange['adjustment']['scope'];
    resourceId: string;
    /** The item is outside the price list: the whole save runs directly (see confirmAdjustment). */
    isDraftItem?: boolean;
    save: (values: V, options: {isPriceHeldBack: boolean}) => Promise<unknown>;
    onDone: () => void;
  }) => {
    const storedPrice = form.storedValues[form.priceField];
    const otherValues: V = {...form.values, [form.priceField]: storedPrice};
    const isConfirming =
      !isSamePrice(form.values[form.priceField], storedPrice) &&
      (await confirmAdjustment(
        {
          kind: 'set',
          value: form.values[form.priceField] || ZERO_PRICE,
          scope: form.scope,
          resourceIds: [form.resourceId],
        },
        {
          saveFirst:
            form.isSavePending || !equals(otherValues, form.storedValues)
              ? {
                  saved: 'otherChanges',
                  run: () => form.save(otherValues, {isPriceHeldBack: true}),
                }
              : undefined,
          isDraftItem: form.isDraftItem,
          onDone: form.onDone,
        }
      ));
    if (!isConfirming) {
      const saved = await form.save(form.values, {isPriceHeldBack: false}).catch(rejectDirectWrite);
      if (saved === ITEM_CHANGE_CANCELLED) {
        return false;
      }
      form.onDone();
    }
    return isConfirming;
  };

  /**
   * A create, archive or inclusion switch of a priced item. A published year asks for a new
   * pricelist version first (D88); a draft year, or a write that changes no price list content
   * (no items, e.g. an item outside the price list), is sent at once.
   * Resolves 'written' (direct), 'writtenWithVersion' (after the dialog) or ITEM_CHANGE_CANCELLED;
   * rejects with the write's ApiError, or with a PricedItemGroupRefusal for a write without rows of
   * a target the open group holds.
   */
  const writePricedItemChange = async (options: {
    /** Defaults to the hook's model year; NewCatalogueEquipment passes the year chosen in its form. */
    modelYearId?: string;
    items: PricedItemChange[];
    /** Sends the write; `pricelistVersion` is set only after the dialog confirmed it. */
    write: (pricelistVersion?: PricelistVersionRequestBody) => Promise<unknown>;
    /** With an open group of the year, the change joins the group instead of being sent (D107). */
    entry?: Omit<PricedItemBatchEntry, 'items'>;
    /** Names an item that a create of the group makes: joins the group also without rows. */
    namesGroupedItem?: boolean;
  }): Promise<PricedItemChangeOutcome> => {
    const writeDirectly = () =>
      options
        .write(undefined)
        .then(() => 'written' as const)
        .catch(rejectDirectWrite);
    const yearId = options.modelYearId ?? modelYearId;
    if (isNil(yearId)) {
      return writeDirectly();
    }
    const {entry} = options;
    if (!isNil(entry) && (await isGroupOpen(yearId))) {
      if (options.items.length > 0 || options.namesGroupedItem === true) {
        return addToGroup(yearId, [{...entry, items: options.items}]);
      }
      // A write without rows changes no price list content and is sent at once, also in a group,
      // but not to a target the group holds: the group would send a stale write (addEntriesToGroup).
      if (isTargetInGroup(yearId, entry)) {
        throw new PricedItemGroupRefusal('targetTaken');
      }
    }
    // Waits for the first load, so a write made before the status is known is not sent directly.
    const known = await readKnownYear(yearId);
    if (known.status !== 'active' || options.items.length === 0) {
      return writeDirectly();
    }
    return confirmItemChange({yearId, items: options.items, submit: options.write});
  };

  /**
   * As rejectDirectWrite; a stale version (DMS-NV-CT-0012) also reads the year and the lists again,
   * because the batch failed as a whole. `onEntryFailed` gets the entry the error names, if any.
   */
  const rejectBatch = (
    error: unknown,
    named?: {operationEntryIndexes: number[]; onEntryFailed?: (entryIndex: number) => void}
  ) => {
    const entryIndex = isNil(named)
      ? undefined
      : failedEntryIndex(error, named.operationEntryIndexes);
    if (!isNil(entryIndex)) {
      named?.onEntryFailed?.(entryIndex);
    }
    if (catalogueErrorCodes(error).includes(STALE_CATALOGUE_VERSION)) {
      dispatch(
        newVehicleApi.util.invalidateTags([
          {type: 'NewVehicleCatalogueResource', id: 'modelYear'},
          'NewVehicleCatalogueAuthoring',
        ])
      );
    }
    return rejectDirectWrite(error);
  };

  /**
   * Sends the entries as one batch: on a published year with rows after one dialog that lists the
   * rows of every entry under its title and asks for one pricelist version, else at once. A batch
   * of more than MAX_BATCH_OPERATIONS operations is refused before anything is sent.
   */
  const writeBatch = async (options: {
    yearId: string | undefined;
    entries: PricedItemBatchEntry[];
    onEntryFailed?: (entryIndex: number) => void;
  }): Promise<PricedItemChangeOutcome> => {
    const built = await buildBatchOperations(options.entries, {isSentAtOnce: true});
    if (built.outcome === 'buildFailed') {
      throw built.error;
    }
    if (built.operations.length === 0) {
      return 'written';
    }
    if (built.operations.length > MAX_BATCH_OPERATIONS) {
      throw new PricedItemGroupRefusal('batchTooLarge');
    }
    const {yearId} = options;
    if (isNil(yearId)) {
      throw new Error('A batch of priced item changes needs its model year.');
    }
    const named = {
      operationEntryIndexes: built.operationEntryIndexes,
      onEntryFailed: options.onEntryFailed,
    };
    const items = rowsOf(built);
    // Waits for the first load, so a batch made before the status is known is not sent directly.
    const known = await readKnownYear(yearId);
    if (known.status !== 'active' || items.length === 0) {
      return sendBatch(yearId, built.operations)
        .then(() => 'written' as const)
        .catch((error: unknown) => rejectBatch(error, named));
    }
    return confirmItemChange({
      yearId,
      items,
      groups: built.groups,
      submit: (pricelistVersion) =>
        sendBatch(yearId, built.operations, pricelistVersion).catch((error: unknown) =>
          rejectBatch(error, named)
        ),
    });
  };

  /**
   * A save that changes several priced items at once (new equipment on N trims, the equipment matrix
   * save, Add from library) as one batch (writeBatch, D107). With an open group of a published year,
   * the entries with rows join the group instead and resolve 'grouped' (addEntriesToGroup).
   * Rejects with the batch's ApiError, after `onEntryFailed` named the entry whose operation the
   * error names (none for an error of the commit).
   */
  const writePricedItemChanges = async (options: {
    /** Defaults to the hook's model year; NewCatalogueEquipment passes the year chosen in its form. */
    modelYearId?: string;
    entries: PricedItemBatchEntry[];
    onEntryFailed?: (entryIndex: number) => void;
  }): Promise<PricedItemChangeOutcome> => {
    const yearId = options.modelYearId ?? modelYearId;
    const withRows = options.entries.filter((entry) => entry.items.length > 0);
    if (isNil(yearId) || withRows.length === 0 || !(await isGroupOpen(yearId))) {
      return writeBatch({yearId, entries: options.entries, onEntryFailed: options.onEntryFailed});
    }
    return addEntriesToGroup({...options, yearId, withRows, writeBatch});
  };

  /**
   * "Review and confirm" of the open group of the year (spec 7.2 step 6): builds every entry from
   * fresh reads, opens one dialog with a heading and "Remove from group" per entry, and sends one
   * batch with the version. Once the batch is written, the sent entries leave the group, and the
   * group closes when no entry is left: a change added meanwhile stays. A failed build rejects with
   * PricedItemGroupBuildFailure, which names the entry; a refusal outside the dialog rejects; both
   * keep the group. A refusal that names an entry keeps the dialog open and names it. A batch whose
   * outcome is unknown stays in the group (see resolveUnknownOutcome).
   */
  const confirmPricedItemGroup = async (groupYearId?: string): Promise<PricedItemChangeOutcome> => {
    const yearId = groupYearId ?? modelYearId;
    const openGroup = isNil(yearId) ? undefined : pricedItemGroupStore.get(yearId);
    if (isNil(yearId) || isNil(openGroup)) {
      return ITEM_CHANGE_CANCELLED;
    }
    const resolved = await resolveUnknownOutcome(yearId, openGroup);
    if (!isNil(resolved)) {
      return resolved;
    }
    const groupBatch = await buildGroupBatch(yearId);
    if (groupBatch === 'empty') {
      return ITEM_CHANGE_CANCELLED;
    }
    if (groupBatch === 'written') {
      return groupBatch;
    }
    const {entries, built} = groupBatch;
    const review = reviewedGroupBatch(yearId, entries, built);
    const known = await readKnownYear(yearId);
    const outcome =
      known.status !== 'active' || rowsOf(built).length === 0
        ? await sendBatch(yearId, built.operations)
            .then(() => 'written' as const)
            .catch((error: unknown) => rejectBatch(error))
        : await confirmItemChange({
            yearId,
            items: rowsOf(built),
            groups: built.groups,
            submit: (pricelistVersion) =>
              review
                .operations()
                .then((operations) => sendBatch(yearId, operations, pricelistVersion))
                .catch((error: unknown) => {
                  keepUnknownOutcome(error, {yearId, sent: pricelistVersion, review});
                  return rejectBatch(error);
                }),
            failureMessage: review.failureMessage,
            onRemoveEntry: review.removeEntry,
          });
    if (outcome !== ITEM_CHANGE_CANCELLED) {
      pricedItemGroupStore.removeSent(yearId, review.sentEntries());
    }
    return outcome;
  };

  /** Whether the year (default: the hook's) is published; waits for the first load, as a write does. */
  const readIsPublished = async (yearId = modelYearId) =>
    isNotNilOrEmpty(yearId) && (await readKnownYear(yearId)).status === 'active';

  /**
   * An editor save with a price per trim. On a published year the changed rows that were stored
   * are held back at their stored prices while everything else saves, then the confirm sets them.
   * One adjustment sets one price, so rows changed to different prices are refused before anything
   * is sent. `save` may ask for a new pricelist version of rows it adds or drops (D102). Resolves
   * how the save went; rejects with a PricedItemGroupRefusal for a changed row with an open group.
   */
  const saveRows = async <V>(form: {
    values: V;
    /** `values` with every changed row back at its stored price. */
    heldBackValues: V;
    storedValues: V;
    changed: {priceNet: string; priceTargetId: string}[];
    isSavePending?: boolean;
    scope: 'colourTrim' | 'packageTrim';
    save: (values: V) => Promise<PricedItemChangeOutcome>;
    onDone: () => void;
  }): Promise<
    | 'saved'
    | 'savedWithVersion'
    | 'grouped'
    | typeof ITEM_CHANGE_CANCELLED
    | 'confirming'
    | 'onePriceAtATime'
  > => {
    const [first] = form.changed;
    const known = isNil(first) || isNil(modelYearId) ? undefined : await readKnownYear(modelYearId);
    const outcome = match({first, status: known?.status})
      .with({first: Pattern.nonNullable, status: 'active'}, ({first: price}) =>
        form.changed.some(({priceNet}) => !isSamePrice(priceNet, price.priceNet))
          ? ({kind: 'onePriceAtATime'} as const)
          : ({kind: 'confirming', priceNet: price.priceNet} as const)
      )
      .otherwise(() => ({kind: 'saved'}) as const);
    return match(outcome)
      .with({kind: 'saved'}, async () => {
        const written = await form.save(form.values);
        if (written === ITEM_CHANGE_CANCELLED) {
          return written;
        }
        form.onDone();
        return match(written)
          .with('writtenWithVersion', () => 'savedWithVersion' as const)
          .with('grouped', () => 'grouped' as const)
          .otherwise(() => 'saved' as const);
      })
      .with({kind: 'onePriceAtATime'}, () => Promise.resolve('onePriceAtATime' as const))
      .with({kind: 'confirming'}, async ({priceNet}) => {
        await confirmAdjustment(
          {
            kind: 'set',
            value: priceNet,
            scope: form.scope,
            resourceIds: form.changed.map(({priceTargetId}) => priceTargetId),
          },
          {
            saveFirst:
              form.isSavePending || !equals(form.heldBackValues, form.storedValues)
                ? {saved: 'otherChanges', run: () => form.save(form.heldBackValues)}
                : undefined,
            onDone: form.onDone,
          }
        );
        return 'confirming' as const;
      })
      .exhaustive();
  };

  const dialogProps: NewPricelistVersionDialogProps = {
    change,
    onClose: () => setChange(undefined),
  };
  return {
    isPublished: modelYear?.status === 'active',
    readIsPublished,
    save,
    saveRows,
    confirmAdjustment,
    writePricedItemChange,
    writePricedItemChanges,
    confirmPricedItemGroup,
    rejectDirectWrite,
    dialogProps,
  };
}
